/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Button from '@material-ui/core/Button';
import {ColDef, ColumnState} from 'ag-grid-community';
import debounce from 'lodash.debounce';
import isUndefined from 'lodash.isundefined';
import {viewDeviceById} from '@ion/applications/src/pages/devices/state';
import {errorOptions} from '@ion/applications/src/prompts/constants';
import {exportDevicesStatus} from '@ion/applications/src/store/actions/exporter';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {ExportContext} from '@ion/applications/src/types/redux';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Asset,
  CRUDPermissions,
  Device,
  DownloadJob,
  TaskStatus
} from '@ion/core/src/types/entities';
import {formatDateTime, timeDiff} from '@ion/core/src/utils/DateUtil';
import {getDeviceIdentifierFromType} from '@ion/core/src/utils/DeviceUtils';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {SearchField} from '@ion/dls/src/components/search/SearchField';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {EmailExportRequestDialog} from '@ion/dls/src/export/EmailExportRequestDialog';
import {
  AgGridType,
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  AG_CLASSES,
  getPageProxy,
  SelectAllCheckbox
} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useGridEventListener} from '@ion/dls/src/grid/useGridEventListener';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {GridRefreshIcon} from '@ion/dls/src/icons/GridRefreshIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {DLSTheme} from '@ion/dls/src/types/types';
import {cancelDevices, getDevicesStatus} from '@/store/actions/downloadjobs';
import JobMeta from './JobMeta';
import RemoveDeviceButton from './RemoveDeviceButton';

export interface DownloadJobStatusGridProps extends
  SelectAllCheckbox,
  DeviceStatusEmailExportProps {
  devicesstatus?: Asset;
  refreshPage?: boolean;
  setRefreshPage?: Function;
  onRefresh?: MouseEventHandler<HTMLButtonElement>;
  index?: number;
  gridRefresh?: boolean;
  jobsPermissions?: CRUDPermissions;
  deviceStatusDialogClose?: Function;
  jobStatusDialogClose?: Function;
  onRemoveDevice?: (device: Asset) => void;
  removeDeviceResult?: DownloadJob | null;
}

export interface DeviceStatus extends TaskStatus {
  device?: Device;
  esn?: string;
  typeName?: string;
  identifier?: string;
}

interface DeviceStatusEmailExportProps {
  exportLimit?: number;
  isExporting?: boolean;
  enableExport?: boolean;
  exportContext?: ExportContext;
  gridExportCount?: number;
  currentAccountId?: number;
  isAboveExportLimit?: boolean;
  areTotalRowsSelected?: boolean;
  isEmailExportRequest?: boolean;
  onComplete?: (newMessage: string) => void;
  setTotalRowsCount?: (totalRowsCount: number) => void;
  setIsEmailExportRequest?: (isEmailExportRequest: boolean) => void;
  setSelectedDeviceStatus?: (selectedDeviceStatus: DownloadJob[]) => void;
  onCloseEmailExportDialog?: () => void;
}

// Styles for the Job Container
const useStyles = (theme: DLSTheme) => ({
  searchField : {
    width                          : '40%',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px',
      width       : '100%'
    }
  },
  filterSection : {
    display        : 'flex',
    padding        : '7px 1px',
    justifyContent : 'flex-start'
  },
  formIcons : {
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'revert',
    marginRight    : '-8px'
  },
  refreshButtonRoot : {
    height    : '41px',
    marginTop : '5px',
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  },
  exportIcon : {
    display        : 'flex',
    justifyContent : 'end'
  },
  downloadJobsStatusGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      maxHeight                : '100vh',
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto',
        maxHeight : '300px'
      }
    }
  }
});

const searchBytext = ['typeName', 'esn', 'serialNumber'];

/**
 * Grid to be rendered on the Provisioning Jobs page.
 */
export const DownloadJobStatusGrid = ({
  devicesstatus,
  setRefreshPage,
  jobsPermissions,
  onRefresh,
  index,
  deviceStatusDialogClose,
  jobStatusDialogClose,
  onRemoveDevice,
  removeDeviceResult,
  onComplete,
  getAreTotalRowsSelected,
  setTotalRowsCount,
  setSelectedDeviceStatus,
  setIsEmailExportRequest,
  onCloseEmailExportDialog,
  currentAccountId,
  exportContext,
  exportLimit = 0,
  gridExportCount = 0,
  refreshPage = false,
  gridRefresh = false,
  areTotalRowsSelected = false,
  isAboveExportLimit = false,
  isExporting = false,
  enableExport = false,
  isEmailExportRequest = false
}: DownloadJobStatusGridProps, ref) => {
  const dispatch                              = useDispatch();
  const containerStyles                       = useExtendStyles(useStyles);
  const classes                               = containerStyles();
  const columnState: ColumnState[]            = useSelector(({downloadjobs: {grid}}: DownloadJobStatusGridProps) =>
    grid.columnState);
  const taskRef                               = useRef<IonGridAPI>();
  const [refreshRequired, setRefreshRequired] = useState(refreshPage);
  const [exportDetails, setExportDetails]     = useState({page: 1, pageSize: 20, index: 1});
  const [searchValue, setSearchValue]         = useState<any>();
  const [searchProperty, setSearchProperty]   = useState<any>(searchBytext);
  const [deviceStatus, setDeviceStatus]       = useState(false);

  const {
    definitions,
    defaultDefinitions
  } = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {columnDefinitions}                     = useColumnChooser(definitions);
  const [showStatusPrompt, setShowStatusPrompt] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice]     = useState<Asset[]>();
  const [gridApi, setGridApi]                   = useState<AgGridType>(null);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    onExport,
    onRefresh,
    getExportColumnDefinitions
  }));

  useEffect(() => {
    setRefreshRequired(refreshPage);
    setRefreshPage(false);
    setDeviceStatus(true);
    return function() {
      dispatch(cancelDevices(null, null, false));
    };
  }, [refreshPage]);

  useEffect(() => {
    if (removeDeviceResult) {
      setSelectedDevice([]);
    }
  }, [removeDeviceResult]);

  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  const getExportColumnDefinitions = useCallback(() => {
    const ionGridData = taskRef?.current?.getExportParams() || {};
    const {columns}   = ionGridData || {};
    if (columns) {
      const definitions = {};
      columns.forEach(({headerName, field}) => {
        if (headerName) {
          const fieldLabel = attributeMappings[field] || field;
          definitions[fieldLabel] = headerName;
        }
      });
      return {columns: definitions};
    }
    return {columns: {}};
  }, []);

  const attributeMappings = {
    identifier : 'identifier',
    errorCodes : 'failedReason',
    status     : 'progressState',
    updateTime : 'lastUpdate'
  };

  const onExport = useCallback(function() {
    if (isAboveExportLimit) {
      setIsEmailExportRequest?.(true);
    } else if (gridExportCount) {
      const params                = taskRef.current?.getExportParams(false);
      params.items                = taskRef.current?.getSelectedColumns();
      params.exportLimit          = exportLimit;
      params.gridExportCount      = gridExportCount;
      params.areTotalRowsSelected = areTotalRowsSelected;
      dispatch(exportDevicesStatus(params, devicesstatus, exportDetails) as any);
    }
  }, [isAboveExportLimit, gridExportCount, areTotalRowsSelected, exportLimit]);

  const onSearchChange = useCallback((e: ChangeEvent<{name?: string; value: string;}>) => {
    e.persist();
    debounceFieldChange(e.target.value);
    setDeviceStatus(false);
  }, []);

  const debounceFieldChange = useCallback(debounce((value: string) => {
    setSearchProperty(searchBytext);
    setSearchValue(value);
    const hasValue = value !== '';
    setDeviceStatus(!hasValue);
  }, 300), [searchBytext]);

  function getDevicesJobs({
    page,
    pageSize,
    callback
  }) {
    const pageNumber = deviceStatus ? page + 1 : getPageProxy(gridApi, 0)?.currentPage + 1;
    setExportDetails({page: page + 1, pageSize, index});
    dispatch(getDevicesStatus({
      devicesstatus,
      pageId   : pageNumber,
      callback : (results) => {
        setTotalRowsCount(results.totalCount);
        callback(results);
      },
      pageSize,
      index
    }) as any);
  };

  const gridHeight = useMemo(() => {
    return function() {
      return classes.downloadJobsStatusGrid;
    };
  }, []);

  const columnComponents = useMemo(() => ({
    deviceCell : (item: GridResult) => (
      <LinkCell data={item}
        onClick={onDeviceClick} />
    ),
    errorCodesCell : (item: GridResult) => (
      <JobMeta data={item} />
    )
  }), [devicesstatus]);

  const onDeviceClick = useCallback(({data: {device, error}}: GridResult) => {
    const createdDate = formatDateTime(devicesstatus?.createdOn, true, true);
    const currentDate = formatDateTime(Date(), true, true);
    const secDiff     = timeDiff(currentDate, createdDate, 'seconds');
    if (secDiff <= 25) {
      setShowStatusPrompt(true);
    } else {
      if (isUndefined(device)) {
        dispatch(toggleUserPrompt({
          ...errorOptions,
          actionLabel : translate('common:ok'),
          message     : error
        }));
      } else {
        deviceStatusDialogClose();
        jobStatusDialogClose();
        dispatch(viewDeviceById(entityId(device)) as any);
      }
    }
  }, []);

  const onDismiss = useCallback(() => {
    setShowStatusPrompt(false);
  }, []);

  const searchCondition = useCallback((searchVal) => {
    return !isEqual(searchVal, '') && !isEqual(searchVal, undefined);
  }, []);

  /* onSelectionChanged method pass as an argumnet inside useGridEventListener hook.
   And should be called when the passed grid event ('selectionChanged') fires. */
  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    const rowsSelectedWithId = rowsSelected.map(row => ({
      ...row,
      id : row.identifier
    }));
    setSelectedDevice(getUniqItems(rowsSelectedWithId));
    setSelectedDeviceStatus?.(getUniqItems(rowsSelectedWithId));
  }, []);

  useGridEventListener(taskRef, 'selectionChanged', onSelectionChanged);

  //This RemoveDeviceButton will be visible on grid item's checkbox selection..
  const getActionMenu = useMemo(() => (
    <RemoveDeviceButton selectedDevice={selectedDevice}
      downloadjobs={devicesstatus}
      onRemoveDevice={onRemoveDevice}
      jobsPermissions={jobsPermissions} />
  ), [selectedDevice]);

  return (
    <>
      <div className={classes.filterSection}>
        <div className={classes.searchField}>
          <SearchField placeholder={translate('downloadjobs:searchPlaceHolder')}
            onChange={onSearchChange} />
        </div>
        <div className={classes.formIcons}>
          <Button onClick={onRefresh}
            className={classes.refreshButtonRoot}
            disableRipple>
            <GridRefreshIcon />
          </Button>
        </div>
        <div className={classes.exportIcon}>
          <CSVExportButton onExport={onExport}
            isExporting={isExporting}
            entity={exportContext}
            enableExport={enableExport}
            exportLimit={isAboveExportLimit}
            disableExport={true} />
        </div>
      </div>
      <IonGrid showBulkHeader
        ref={taskRef}
        paginationEnabled
        showCustomPaginationBar
        onGridReady={onGridReady}
        searchCondition={searchCondition}
        customSearch={searchValue}
        searchebleColumns={searchProperty}
        refreshRequired={refreshRequired || gridRefresh}
        setRefreshRequired={setRefreshRequired}
        loadItems={getDevicesJobs}
        augmentItems={constructDownloadJobStatusGridRows}
        getGridHeight={gridHeight}
        columnComponents={columnComponents}
        enableSelectAllCheckbox={true}
        isPersist={true}
        getAreTotalRowsSelected={getAreTotalRowsSelected}
        isTotalRowsSelected={areTotalRowsSelected}
        actionMenuItems={getActionMenu}
        columnDefinitions={columnDefinitions}
        defaultDefinitions={defaultDefinitions}
        gridOptions={{onSelectionChanged}}
        pageSizes={PAGE_SIZES} />
      {showStatusPrompt && (
        <UserNotificationDialog show
          capitalize
          title={devicesstatus?.name}
          message={translate('downloadjobs:jobStatus.devicesCountStatus')}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss}/>
      )}
      <ConditionalRender condition={isEmailExportRequest}>
        <EmailExportRequestDialog exportCount={gridExportCount}
          show={isEmailExportRequest}
          exportContext={ExportContext.device}
          getExportColumnDefinitions={getExportColumnDefinitions}
          isDeviceTask={true}
          taskIndex={index}
          jobId={entityId(devicesstatus?._self?.href)}
          accountId={currentAccountId}
          showSnackbar={onComplete}
          onClose={onCloseEmailExportDialog} />
      </ConditionalRender>
    </>
  );
};

/**
 * Converts a collection of jobs and converts them to a list of items specific
 * for display within the Provisioning Jobs Grid, while retaining all of the items existing
 * attributes,
 */
export const constructDownloadJobStatusGridRows = (devicesStatus: DeviceStatus[]): GridResult[] => {
  return devicesStatus.map((deviceStatus: DeviceStatus) => {
    const device     = deviceStatus?.device;
    const identifier = deviceStatus?.identifier;
    return {
      ...deviceStatus,
      identifier : identifier || getDeviceIdentifierFromType(device),
      updateTime : formatDateTime(deviceStatus?.updateTime) || translate('common:notAvailable')
    };
  }) as GridResult[];
};

/**
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionDesktop = (): ColDef[] => {
  return [{
    headerName   : translate('downloadjobs:devicesStatus.mobileId'),
    field        : 'identifier',
    cellRenderer : 'deviceCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : false
  }, {
    headerName : translate('downloadjobs:devicesStatus.lastUpdate'),
    field      : 'updateTime',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:devicesStatus.progressStatus'),
    field      : 'status',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('downloadjobs:devicesStatus.failedReason'),
    field              : 'errorCodes',
    cellRenderer       : 'errorCodesCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }];
};

/**
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionMobile = (): ColDef[] => {
  return [{
    headerName   : translate('downloadjobs:devicesStatus.mobileId'),
    field        : 'esn',
    cellRenderer : 'deviceCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : false
  }, {
    headerName : translate('downloadjobs:devicesStatus.lastUpdate'),
    field      : 'updateTime',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('downloadjobs:devicesStatus.progressStatus'),
    field      : 'status',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('downloadjobs:devicesStatus.failedReason'),
    field              : 'errorCodes',
    cellRenderer       : 'errorCodesCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }];
};

const getColumnDefinitions = () => ({
  desktop : getColumnDefinitionDesktop(),
  mobile  : getColumnDefinitionMobile()
});

export default forwardRef(DownloadJobStatusGrid);
